import { useCallback, useState, useEffect } from 'react';

let sharedAudioCtx = null;

export default function useAlertAudio() {
  const [isSuspended, setIsSuspended] = useState(false);

  const getCtx = () => {
    if (typeof window === 'undefined') return null;
    if (!sharedAudioCtx) {
      sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return sharedAudioCtx;
  };

  useEffect(() => {
    const ctx = getCtx();
    if (ctx) {
      setIsSuspended(ctx.state === 'suspended');
      const handleStateChange = () => setIsSuspended(ctx.state === 'suspended');
      ctx.addEventListener('statechange', handleStateChange);
      return () => ctx.removeEventListener('statechange', handleStateChange);
    }
  }, []);

  const playAlertSound = useCallback((customUrl = null) => {
    const audioCtx = getCtx();
    if (!audioCtx) return;

    // If a custom URL is provided, try to play it via a standard Audio element
    if (customUrl) {
      const audio = new Audio(customUrl);
      audio.crossOrigin = "anonymous";
      audio.play().catch(err => {
        console.warn("Failed to play custom sound, falling back to generated beep:", err);
        playGeneratedBeep(audioCtx);
      });
      return;
    }

    // Fallback: Generate a high-quality "Ding" procedurally
    playGeneratedBeep(audioCtx);
  }, []);

  const playGeneratedBeep = (audioCtx) => {
    if (audioCtx.state === 'suspended') {
      console.warn("AudioContext suspended. Interaction required.");
      return;
    }

    const masterGain = audioCtx.createGain();
    masterGain.connect(audioCtx.destination);
    masterGain.gain.setValueAtTime(0.3, audioCtx.currentTime);

    // Simple "Ding" logic
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    
    osc.connect(gain);
    gain.connect(masterGain);
    
    osc.type = 'sine';
    // Start at a higher frequency and drop slightly for a "bell" effect
    osc.frequency.setValueAtTime(880, audioCtx.currentTime); // A5 note
    osc.frequency.exponentialRampToValueAtTime(800, audioCtx.currentTime + 0.3);
    
    gain.gain.setValueAtTime(0.5, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.5);
    
    osc.start();
    osc.stop(audioCtx.currentTime + 0.5);
  };

  const resumeAudio = useCallback(async () => {
    const ctx = getCtx();
    if (ctx && ctx.state === 'suspended') {
      await ctx.resume();
    }
  }, []);

  return { playAlertSound, resumeAudio, isSuspended };
}
